import { useTranslation } from 'react-i18next';
import { GripVertical, Trash2, Edit2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Button } from '@/components/ui';
import type { Widget } from '@/types';

interface WidgetItemProps {
  widget: Widget;
  onEdit: (widget: Widget) => void;
  onDelete: (id: string) => void;
}

const WidgetItem = ({ widget, onEdit, onDelete }: WidgetItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const { t } = useTranslation();

  const getWidgetLabel = () => {
    switch (widget.type) {
      case 'link':
        return (widget.data as any).title || t('widgets.link');
      case 'text':
        return (widget.data as any).content?.slice(0, 30) + '...' || t('widgets.text');
      case 'photo':
        return (widget.data as any).caption || t('widgets.photo');
      case 'product':
        return (widget.data as any).title || t('widgets.product');
      case 'social':
        return `${(widget.data as any).platform} - @${(widget.data as any).username}`;
      case 'music':
        return (widget.data as any).title || t('widgets.music');
      default:
        return t('widgets.widget');
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card variant="default" padding="none" className="flex items-center gap-3 p-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Widget Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-dark-800 dark:text-gray-100 capitalize text-sm">
            {widget.type}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {getWidgetLabel()}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(widget)}
            className="p-2"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(widget.id)}
            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

interface WidgetListProps {
  widgets: Widget[];
  onReorder: (widgetIds: string[]) => Promise<unknown>;
  onEdit: (widget: Widget) => void;
  onDelete: (id: string) => void;
}

const WidgetList = ({ widgets, onReorder, onEdit, onDelete }: WidgetListProps) => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedWidgets.findIndex((w) => w.id === active.id);
      const newIndex = sortedWidgets.findIndex((w) => w.id === over.id);

      const newOrder = arrayMove(sortedWidgets, oldIndex, newIndex);
      await onReorder(newOrder.map((w) => w.id));
    }
  };

  if (widgets.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{t('profile.noWidgetsYet')}</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedWidgets.map((w) => w.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {sortedWidgets.map((widget) => (
            <WidgetItem
              key={widget.id}
              widget={widget}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default WidgetList;
